/**
 * OIMS v2 - Status/Health monitoring tab (Card Grid Dashboard)
 * Groups, auto-refresh with SVG countdown ring, health details & BIRT debug modals
 */

import { escapeHtml } from './utils.js';

// DOM references
let selHealthGroup, selRefreshInterval, btnRefreshHealth, btnGroupChart, btnRetryHealth;
let healthLastUpdate, statusCardGrid, statusSkeleton, statusEmpty, statusError, statusErrorMsg;
let heroHealthy, heroTotal, pillHealthy, pillDegraded, pillUnhealthy, pillDangerWrap;
let refreshRingProgress, refreshCountdown, refreshTimerWrap;
let pillDropdown, statusPills;

// State
let healthAutoRefreshTimer = null;
let healthCountdownTimer = null;
let healthNextRefreshTime = null;
let currentHealthData = null;
let isLoadingHealth = false;

// Group icons
const GROUP_ICONS = {
  birt_instances: 'bi-file-earmark-bar-graph',
  wildfly_instances: 'bi-server',
  jbpm_instances: 'bi-gear-wide-connected',
  org_instances: 'bi-box-seam',
  other_docker_instances: 'bi-boxes',
  iis_instances: 'bi-window-stack',
  sql_instances: 'bi-database',
};

// SVG ring circumference (2 * PI * 16)
const RING_CIRCUMFERENCE = 100.53;

export function initStatus(state) {
  selHealthGroup = document.getElementById("selHealthGroup");
  selRefreshInterval = document.getElementById("selRefreshInterval");
  btnRefreshHealth = document.getElementById("btnRefreshHealth");
  btnGroupChart = document.getElementById("btnGroupChart");
  btnRetryHealth = document.getElementById("btnRetryHealth");
  healthLastUpdate = document.getElementById("healthLastUpdate");
  statusCardGrid = document.getElementById("statusCardGrid");
  statusSkeleton = document.getElementById("statusSkeleton");
  statusEmpty = document.getElementById("statusEmpty");
  statusError = document.getElementById("statusError");
  statusErrorMsg = document.getElementById("statusErrorMsg");
  heroHealthy = document.getElementById("heroHealthy");
  heroTotal = document.getElementById("heroTotal");
  pillHealthy = document.getElementById("pillHealthy");
  pillDegraded = document.getElementById("pillDegraded");
  pillUnhealthy = document.getElementById("pillUnhealthy");
  pillDangerWrap = document.getElementById("pillDangerWrap");
  refreshRingProgress = document.getElementById("refreshRingProgress");
  refreshCountdown = document.getElementById("refreshCountdown");
  refreshTimerWrap = document.getElementById("refreshTimerWrap");
  pillDropdown = document.getElementById("pillDropdown");
  statusPills = document.getElementById("statusPills");

  if (!selHealthGroup) return;

  // Init BIRT analytics sub-section
  initBirtAnalytics();

  // Group selector
  selHealthGroup.addEventListener("change", () => {
    stopHealthAutoRefresh();
    // Show/hide BIRT analytics based on group
    showBirtAnalytics(selHealthGroup.value === 'birt_instances');
    if (selHealthGroup.value) {
      showSkeleton();
      loadHealthStatus(state, true);
    } else {
      showEmpty();
      healthLastUpdate.textContent = "Not checked yet";
      updateHeroPills(null);
    }
  });

  // Refresh interval change
  selRefreshInterval && selRefreshInterval.addEventListener("change", () => {
    stopHealthAutoRefresh();
    if (selHealthGroup.value) {
      scheduleHealthRefresh(state);
    }
  });

  // Manual refresh
  btnRefreshHealth && btnRefreshHealth.addEventListener("click", () => {
    if (selHealthGroup.value) {
      stopHealthAutoRefresh();
      loadHealthStatus(state, true);
    }
  });

  // Retry on error
  btnRetryHealth && btnRetryHealth.addEventListener("click", () => {
    if (selHealthGroup.value) {
      showSkeleton();
      loadHealthStatus(state, true);
    }
  });

  // Event delegation on card grid
  statusCardGrid && statusCardGrid.addEventListener("click", (e) => {
    const btn = e.target.closest("button[data-action]");
    if (!btn) return;
    const action = btn.dataset.action;
    const iid = btn.dataset.iid;
    const name = btn.dataset.name;
    const group = btn.dataset.group;

    if (action === "restart") restartHealthInstance(state, iid, name);
    else if (action === "details") showHealthDetails(iid);
    else if (action === "charts") {
      document.dispatchEvent(new CustomEvent('oims:show-charts', {
        detail: { instanceId: iid, name, group }
      }));
    }
    else if (action === "birt-debug") showBirtDebug(iid, name);
  });

  // Group header collapse toggle (delegation)
  statusCardGrid && statusCardGrid.addEventListener("click", (e) => {
    const header = e.target.closest(".status-group-header");
    if (!header) return;
    const group = header.closest(".status-group");
    if (group) group.classList.toggle("collapsed");
  });

  // Tab change listener
  document.addEventListener('oims:tab-change', (e) => {
    if (e.detail.tab !== 'status') stopHealthAutoRefresh();
  });

  // Dashboard navigation: select group + scroll to instance
  let _pendingInstanceHighlight = null;
  document.addEventListener('oims:navigate-status', (e) => {
    const { instanceId, groupId } = e.detail || {};
    if (!groupId || !selHealthGroup) return;

    _pendingInstanceHighlight = instanceId;

    // Select group in dropdown (if not already selected, trigger load)
    if (selHealthGroup.value !== groupId) {
      selHealthGroup.value = groupId;
      selHealthGroup.dispatchEvent(new Event('change'));
    }

    // Wait for render, then scroll to + highlight instance
    const tryHighlight = (attempts = 0) => {
      if (!_pendingInstanceHighlight || attempts > 20) return;
      const card = statusCardGrid?.querySelector(`[data-instance="${CSS.escape(_pendingInstanceHighlight)}"]`);
      if (card) {
        _pendingInstanceHighlight = null;
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.classList.add('status-card-highlight');
        setTimeout(() => card.classList.remove('status-card-highlight'), 3000);
      } else {
        setTimeout(() => tryHighlight(attempts + 1), 200);
      }
    };
    // Small delay for tab switch + data load
    setTimeout(() => tryHighlight(), 300);
  });

  // Pill click -> show affected instances dropdown
  statusPills && statusPills.addEventListener("click", (e) => {
    const pill = e.target.closest("[data-pill-status]");
    if (!pill || !currentHealthData) return;

    const filterStatus = pill.dataset.pillStatus;
    const matches = (currentHealthData.instances || []).filter(i => i.status === filterStatus);

    if (matches.length === 0) {
      pillDropdown.classList.add("d-none");
      return;
    }

    // Position dropdown below the clicked pill
    const rect = pill.getBoundingClientRect();
    const parentRect = pill.closest('.status-hero-left').getBoundingClientRect();
    pillDropdown.style.top = `${rect.bottom - parentRect.top + 4}px`;
    pillDropdown.style.left = `${rect.left - parentRect.left}px`;

    // Build list
    const statusIcon = filterStatus === 'healthy' ? 'bi-check-circle-fill text-success' :
                       filterStatus === 'degraded' ? 'bi-exclamation-triangle-fill text-warning' :
                       'bi-x-circle-fill text-danger';

    let html = `<div class="pill-dropdown-header">${escapeHtml(filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1))} Instances</div>`;
    matches.forEach(inst => {
      const detail = inst.warning || inst.error || '';
      html += `<a class="pill-dropdown-item" href="#" data-instance-id="${escapeHtml(inst.instance_id)}">
        <i class="bi ${statusIcon}"></i>
        <div class="pill-dropdown-item-text">
          <span class="pill-dropdown-name">${escapeHtml(inst.name)}</span>
          ${detail ? `<span class="pill-dropdown-detail">${escapeHtml(detail)}</span>` : ''}
        </div>
        <i class="bi bi-arrow-right"></i>
      </a>`;
    });

    pillDropdown.innerHTML = html;
    pillDropdown.classList.remove("d-none");
  });

  // Pill dropdown item click -> scroll to card
  pillDropdown && pillDropdown.addEventListener("click", (e) => {
    e.preventDefault();
    const item = e.target.closest("[data-instance-id]");
    if (!item) return;

    const iid = item.dataset.instanceId;
    pillDropdown.classList.add("d-none");

    const card = statusCardGrid?.querySelector(`[data-instance="${CSS.escape(iid)}"]`);
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('status-card-highlight');
      setTimeout(() => card.classList.remove('status-card-highlight'), 3000);
    }
  });

  // Click outside to dismiss dropdown
  document.addEventListener("click", (e) => {
    if (pillDropdown && !pillDropdown.contains(e.target) && !e.target.closest("[data-pill-status]")) {
      pillDropdown.classList.add("d-none");
    }
  });
}

export async function loadHealthGroups(state) {
  if (!state.accessToken) return;
  selHealthGroup.innerHTML = '<option value="">Loading groups...</option>';
  selHealthGroup.disabled = true;
  try {
    const { data } = await axios.get("/status/groups");
    selHealthGroup.innerHTML = '<option value="">Select service group...</option>';
    (data.groups || []).forEach(g => {
      const opt = document.createElement("option");
      opt.value = g.id;
      opt.textContent = `${g.name} (${g.instance_count} instances)`;
      selHealthGroup.appendChild(opt);
    });
  } catch (e) {
    console.error("Failed to load health groups:", e);
    selHealthGroup.innerHTML = '<option value="">Failed to load groups</option>';
  } finally {
    selHealthGroup.disabled = false;
  }
}

// --- Loading & State Display ---

function showSkeleton() {
  statusEmpty.classList.add("d-none");
  statusError.classList.add("d-none");
  statusCardGrid.classList.add("d-none");
  statusSkeleton.classList.remove("d-none");
}

function showEmpty() {
  statusSkeleton.classList.add("d-none");
  statusError.classList.add("d-none");
  statusCardGrid.classList.add("d-none");
  statusEmpty.classList.remove("d-none");
  if (btnGroupChart) btnGroupChart.classList.add("d-none");
}

function showError(msg) {
  statusSkeleton.classList.add("d-none");
  statusEmpty.classList.add("d-none");
  statusCardGrid.classList.add("d-none");
  statusError.classList.remove("d-none");
  statusErrorMsg.textContent = msg || "Unable to retrieve instance status.";
  if (btnGroupChart) btnGroupChart.classList.add("d-none");
}

function showCards() {
  statusSkeleton.classList.add("d-none");
  statusEmpty.classList.add("d-none");
  statusError.classList.add("d-none");
  statusCardGrid.classList.remove("d-none");
}

// --- Main Data Load ---

async function loadHealthStatus(state, forceRefresh = false) {
  const groupId = selHealthGroup.value;
  if (!groupId || !state.accessToken || isLoadingHealth) return;

  isLoadingHealth = true;
  try {
    btnRefreshHealth.disabled = true;
    btnRefreshHealth.querySelector("i").classList.add("spin");

    const params = forceRefresh ? { force_refresh: true } : {};
    const { data } = await axios.get(`/status/health/${groupId}`, { params });
    currentHealthData = data;

    renderHealthCards(data, state);
    updateHeroPills(data.summary);
    updateLastCheckTime(data.checked_at);
    showCards();
    if (btnGroupChart) btnGroupChart.classList.remove("d-none");
    scheduleHealthRefresh(state);

  } catch (e) {
    console.error("Failed to load health status:", e);
    showError(e.response?.data?.detail || e.message);
  } finally {
    isLoadingHealth = false;
    btnRefreshHealth.disabled = false;
    btnRefreshHealth.querySelector("i").classList.remove("spin");
  }
}

// --- Render Card Grid ---

function renderHealthCards(data, state) {
  if (!data || !data.instances || data.instances.length === 0) {
    showEmpty();
    return;
  }

  const groupId = data.group || '';
  const isBirt = groupId === 'birt_instances';
  const groupIcon = GROUP_ICONS[groupId] || 'bi-server';
  const groupName = data.name || groupId;

  // Build summary for group health bar
  let hCount = 0, dCount = 0, uCount = 0, kCount = 0;
  data.instances.forEach(inst => {
    if (inst.status === 'healthy') hCount++;
    else if (inst.status === 'degraded') dCount++;
    else if (inst.status === 'unhealthy') uCount++;
    else kCount++;
  });
  const total = data.instances.length;

  // Build group HTML
  let html = `
    <div class="status-group" data-group="${escapeHtml(groupId)}">
      <div class="status-group-header">
        <div class="status-group-header-left">
          <i class="group-icon bi ${escapeHtml(groupIcon)}"></i>
          <h3>${escapeHtml(groupName)}</h3>
          <div class="group-health-bar">
            ${hCount ? `<span class="bar-healthy" style="width:${(hCount/total*100).toFixed(0)}%"></span>` : ''}
            ${dCount ? `<span class="bar-warning" style="width:${(dCount/total*100).toFixed(0)}%"></span>` : ''}
            ${uCount ? `<span class="bar-danger" style="width:${(uCount/total*100).toFixed(0)}%"></span>` : ''}
            ${kCount ? `<span class="bar-unknown" style="width:${(kCount/total*100).toFixed(0)}%"></span>` : ''}
          </div>
        </div>
        <div class="status-group-header-right">
          <span class="group-instance-count">${total} instance${total !== 1 ? 's' : ''}</span>
          <i class="bi bi-chevron-down group-collapse-icon"></i>
        </div>
      </div>
      <div class="status-group-body">
        <div class="status-cards-grid">
  `;

  data.instances.forEach(inst => {
    html += buildInstanceCard(inst, data, isBirt);
  });

  html += `
        </div>
      </div>
    </div>
  `;

  statusCardGrid.innerHTML = html;
}

function buildInstanceCard(inst, data, isBirt) {
  const status = inst.status || 'unknown';
  const eid = escapeHtml(inst.instance_id);
  const ename = escapeHtml(inst.name);
  const egroup = escapeHtml(data.group || '');
  const statusLabel = status === 'healthy' ? 'Healthy' :
                      status === 'degraded' ? 'Degraded' :
                      status === 'unhealthy' ? 'Down' : 'Unknown';

  // Response time badge — platform-aware thresholds
  // Windows/PowerShell is inherently slower (~1400ms normal), Linux shell is fast (~150ms normal)
  const rt = inst.response_time_ms || 0;
  const isWindows = (data.platform === 'windows');
  const rtWarn  = isWindows ? 3000 : 500;
  const rtCrit  = isWindows ? 5000 : 1000;
  const rtClass = rt < rtWarn ? 'response-fast' : rt < rtCrit ? 'response-slow' : 'response-critical';

  // Metrics section
  let metricsHtml = '';
  if (inst.error) {
    metricsHtml = `<div class="status-card-error"><i class="bi bi-exclamation-octagon-fill"></i><span>${escapeHtml(inst.error)}</span></div>`;
  } else {
    if (inst.warning) {
      metricsHtml += `<div class="status-card-warning-msg"><i class="bi bi-exclamation-triangle-fill"></i><span>${escapeHtml(inst.warning)}</span></div>`;
    }
    if (inst.metrics) {
      metricsHtml += buildMetricsHtml(inst.metrics);
    }
  }

  // Action buttons
  const instRestartAction = inst.restart_action || data.restart_action;
  const restartBtn = instRestartAction ? `
    <button class="btn btn-sm btn-card-restart" data-action="restart" data-iid="${eid}" data-name="${ename}" data-restart-action="${escapeHtml(instRestartAction)}" title="Restart ${ename}" aria-label="Restart ${ename}">
      <i class="bi bi-arrow-repeat"></i>
    </button>` : '';

  const debugBtn = isBirt ? `
    <button class="btn btn-sm btn-outline-secondary" data-action="birt-debug" data-iid="${eid}" data-name="${ename}" title="Pool Debug" aria-label="Pool Debug for ${ename}">
      <i class="bi bi-bug"></i>
    </button>` : '';

  return `
    <div class="status-card status-card-${escapeHtml(status)}" data-instance="${eid}" role="article" aria-label="${ename} - ${statusLabel}">
      <div class="status-card-strip"></div>
      <div class="status-card-header">
        <div class="status-card-identity">
          <div class="status-icon-wrap">
            <div class="status-pulse"></div>
            <i class="bi bi-server"></i>
          </div>
          <div>
            <h4 class="status-card-name">${ename}</h4>
            <span class="status-card-id">${eid}</span>
          </div>
        </div>
        <span class="status-label">${escapeHtml(statusLabel)}</span>
      </div>
      ${metricsHtml}
      <div class="status-card-footer">
        <span class="response-badge ${rtClass}">
          <i class="bi bi-lightning-fill"></i>${rt}ms
        </span>
        <div class="status-card-actions">
          ${restartBtn}
          <button class="btn btn-sm btn-outline-secondary" data-action="details" data-iid="${eid}" data-name="${ename}" title="Details" aria-label="Details for ${ename}">
            <i class="bi bi-info-circle"></i>
          </button>
          <button class="btn btn-sm btn-outline-primary" data-action="charts" data-iid="${eid}" data-name="${ename}" data-group="${egroup}" title="Charts" aria-label="Charts for ${ename}">
            <i class="bi bi-graph-up"></i>
          </button>
          ${debugBtn}
        </div>
      </div>
    </div>
  `;
}

function metricRow(icon, value, label, extraClass) {
  const cls = extraClass ? ` ${extraClass}` : '';
  return `<div class="status-metric-row${cls}"><i class="bi ${icon}"></i><span class="metric-val">${value}</span><span class="metric-lbl">${label}</span></div>`;
}

function thresholdClass(value, warnAt, critAt) {
  try {
    const v = parseFloat(value);
    if (isNaN(v)) return '';
    if (v >= critAt) return 'metric-danger';
    if (v >= warnAt) return 'metric-warn';
  } catch (_) {}
  return '';
}

function buildMetricsHtml(m) {
  let rows = '';

  // --- Base metrics (shown for ALL types when present) ---

  // CPU%
  if (m.cpu !== undefined) {
    rows += metricRow('bi-cpu', `${m.cpu}%`, 'CPU', thresholdClass(m.cpu, 85, 95));
  }

  // Memory (prefer % over MB, show both if available)
  if (m.memoryUsagePercent !== undefined) {
    rows += metricRow('bi-memory', `${m.memoryUsagePercent}%`, 'JVM Memory', thresholdClass(m.memoryUsagePercent, 80, 95));
  }
  if (m.memoryPercent !== undefined) {
    rows += metricRow('bi-memory', `${m.memoryPercent}%`, 'System Memory', thresholdClass(m.memoryPercent, 80, 95));
  } else if (m.memoryMB !== undefined) {
    rows += metricRow('bi-memory', `${m.memoryMB} MB`, 'Memory', '');
  }

  // Disk%
  if (m.diskPercent !== undefined) {
    const diskDetail = (m.diskUsed && m.diskTotal) ? ` (${m.diskUsed}/${m.diskTotal})` : '';
    const diskLabel = m.diskMount ? `Disk ${escapeHtml(String(m.diskMount))}` : 'Disk';
    rows += metricRow('bi-hdd', `${m.diskPercent}%${diskDetail}`, diskLabel, thresholdClass(m.diskPercent, 80, 90));
  }

  // Uptime
  if (m.uptime) {
    rows += metricRow('bi-clock-history', escapeHtml(String(m.uptime)), 'Uptime', '');
  }

  // Threads
  if (m.jvmThreads !== undefined) {
    rows += metricRow('bi-layers', `${m.jvmThreads}`, 'JVM Threads', '');
  } else if (m.threads !== undefined || m.activeThreads !== undefined) {
    rows += metricRow('bi-layers', `${m.threads || m.activeThreads}`, 'Threads', '');
  }

  // Active Reports (BIRT v2)
  if (m.activeReports !== undefined) {
    rows += metricRow('bi-file-earmark-text', `${m.activeReports}`, 'Active Reports', '');
  }

  // Dedup stats (BIRT v2)
  if (m.dedupHitsTotal !== undefined) {
    const savedSec = m.dedupSavedMs ? (m.dedupSavedMs / 1000).toFixed(0) : '0';
    const dedupVal = `${m.dedupHitsTotal} hits` + (m.dedupCacheSize > 0 ? ` (${m.dedupCacheSize} active)` : '');
    rows += metricRow('bi-copy', dedupVal, 'Dedup', '');
    const savedLabel = m.dedupSavedMs > 0 ? `${formatDuration(m.dedupSavedMs)} saved` : '0s saved';
    const savedClass = m.dedupSavedMs > 0 ? 'text-success' : '';
    rows += metricRow('bi-clock', savedLabel, 'Dedup Savings', savedClass);
  }

  // Connections
  if (m.activeConnections !== undefined && m.maxConnections !== undefined) {
    rows += metricRow('bi-plug', `${m.activeConnections} / ${m.maxConnections}`, 'Connections', '');
  }

  // PID
  if (m.pid !== undefined) {
    rows += metricRow('bi-hash', `${m.pid}`, 'PID', '');
  }

  // --- Type-specific sections ---

  // IIS app pools
  let poolsHtml = '';
  if (m.app_pools && m.app_pools.length > 0) {
    poolsHtml = '<div class="status-card-pools">';
    m.app_pools.forEach(p => {
      const cls = p.State === 'Started' ? 'pool-badge-started' : 'pool-badge-stopped';
      poolsHtml += `<span class="pool-badge ${cls}">${escapeHtml(p.Name)}: ${escapeHtml(p.State)}</span>`;
    });
    poolsHtml += '</div>';
  }

  // Docker containers
  let containersHtml = '';
  if (m.containers && m.containers.length > 0) {
    containersHtml = '<div class="status-card-containers">';
    m.containers.forEach(c => {
      const parts = [escapeHtml(c.name)];
      if (c.cpu) parts.push(`CPU:${escapeHtml(c.cpu)}%`);
      if (c.memory) parts.push(`Mem:${escapeHtml(c.memory)}%`);
      if (c.uptime) parts.push(escapeHtml(c.uptime));
      containersHtml += `<span class="container-badge">${parts.join(' | ')}</span>`;
    });
    containersHtml += '</div>';
  }

  const baseHtml = rows ? `<div class="status-card-metrics">${rows}</div>` : '';
  return baseHtml + poolsHtml + containersHtml;
}

// --- Hero Pills & Summary ---

function updateHeroPills(summary) {
  const h = summary ? (parseInt(summary.healthy) || 0) : 0;
  const d = summary ? (parseInt(summary.degraded) || 0) : 0;
  const u = summary ? (parseInt(summary.unhealthy) || 0) : 0;
  const total = h + d + u;

  heroHealthy.textContent = h;
  heroTotal.textContent = total;
  pillHealthy.textContent = `${h} Healthy`;
  pillDegraded.textContent = `${d} Degraded`;
  pillUnhealthy.textContent = `${u} Down`;

  // Add pulse to danger pill if there are unhealthy instances
  if (u > 0) {
    pillDangerWrap.classList.add("has-items");
  } else {
    pillDangerWrap.classList.remove("has-items");
  }
}

function updateLastCheckTime(checkedAt) {
  if (checkedAt) {
    const time = new Date(checkedAt).toLocaleTimeString();
    healthLastUpdate.textContent = `Updated: ${time}`;
  }
}

// --- Auto-refresh with SVG Ring ---

function scheduleHealthRefresh(state) {
  if (healthAutoRefreshTimer) clearTimeout(healthAutoRefreshTimer);
  if (healthCountdownTimer) clearInterval(healthCountdownTimer);

  const interval = parseInt(selRefreshInterval.value) || 0;
  if (interval === 0) {
    refreshTimerWrap.style.display = "none";
    return;
  }

  refreshTimerWrap.style.display = "";
  healthNextRefreshTime = Date.now() + (interval * 1000);

  // Update ring every second
  healthCountdownTimer = setInterval(() => {
    const remaining = Math.max(0, Math.ceil((healthNextRefreshTime - Date.now()) / 1000));
    refreshCountdown.textContent = `${remaining}s`;

    // Update SVG ring progress
    const progress = remaining / interval;
    const offset = RING_CIRCUMFERENCE * (1 - progress);
    refreshRingProgress.style.strokeDashoffset = offset;

    if (remaining === 0) clearInterval(healthCountdownTimer);
  }, 1000);

  // Initial ring state
  refreshRingProgress.style.strokeDashoffset = 0;
  refreshCountdown.textContent = `${interval}s`;

  healthAutoRefreshTimer = setTimeout(() => {
    loadHealthStatus(state, false);
  }, interval * 1000);
}

function stopHealthAutoRefresh() {
  if (healthAutoRefreshTimer) {
    clearTimeout(healthAutoRefreshTimer);
    healthAutoRefreshTimer = null;
  }
  if (healthCountdownTimer) {
    clearInterval(healthCountdownTimer);
    healthCountdownTimer = null;
  }
  if (refreshTimerWrap) refreshTimerWrap.style.display = "none";
}

// --- Restart with Confirm Modal ---

async function restartHealthInstance(state, instanceId, instanceName) {
  // Resolve per-instance restart action (for merged groups like birt_v2)
  const card = statusCardGrid?.querySelector(`.status-card[data-instance="${instanceId}"]`);
  const instBtn = card?.querySelector('[data-restart-action]');
  const effectiveAction = instBtn?.dataset.restartAction || currentHealthData?.restart_action;

  if (!effectiveAction) {
    window.toast.error("No restart action configured for this group");
    return;
  }

  // Use the existing confirm modal instead of blocking confirm()
  const modalEl = document.getElementById("confirmActionModal");
  if (modalEl) {
    document.getElementById("confirmActionName").textContent = effectiveAction;
    document.getElementById("confirmInstanceName").textContent = instanceName;
    const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
    const btnConfirm = document.getElementById("btnConfirmExecute");

    // Clone to remove old listeners
    const newBtn = btnConfirm.cloneNode(true);
    btnConfirm.parentNode.replaceChild(newBtn, btnConfirm);

    newBtn.addEventListener("click", () => {
      modal.hide();
      executeRestart(state, instanceId, instanceName);
    });

    modal.show();
    return;
  }

  // Fallback if modal not found
  if (!confirm(`Restart ${instanceName}?`)) return;
  executeRestart(state, instanceId, instanceName);
}

async function executeRestart(state, instanceId, instanceName) {
  // Visual feedback — mark card as restarting
  const card = statusCardGrid.querySelector(`.status-card[data-instance="${instanceId}"]`);
  if (card) card.classList.add("restarting");

  try {
    // Use per-instance restart action if available (e.g. birt_v2 merged into birt group)
    const restartBtn = card?.querySelector('[data-restart-action]');
    const action_id = restartBtn?.dataset.restartAction || currentHealthData.restart_action;
    const url = `/actions/execute?action_id=${encodeURIComponent(action_id)}&instance_id=${encodeURIComponent(instanceId)}`;
    const { data } = await axios.post(url);

    if (data.status === "sent") {
      window.toast.success(`Restart command sent for ${instanceName}`, { title: "Command Sent", duration: 6000 });

      // Silently open SSE stream for audit trail
      if (data.command_id && state.accessToken) {
        openSilentStream(state.accessToken, instanceId, data.command_id, action_id, instanceName);
      }

      setTimeout(() => {
        if (card) card.classList.remove("restarting");
        loadHealthStatus(state, true);
      }, 5000);
    } else {
      if (card) card.classList.remove("restarting");
      window.toast.error("Failed to send restart command: " + (data.message || "unknown error"));
    }
  } catch (e) {
    if (card) card.classList.remove("restarting");
    window.toast.error("Error: " + (e.response?.data?.detail || e.message));
  }
}

function openSilentStream(accessToken, instanceId, commandId, actionId, displayName) {
  const url = new URL("/actions/stream", window.location.origin);
  url.searchParams.set("instance_id", instanceId);
  url.searchParams.set("command_id", commandId);
  url.searchParams.set("access_token", accessToken);
  if (actionId) url.searchParams.set("action_id", actionId);
  if (displayName) url.searchParams.set("display_name", displayName);

  const sse = new EventSource(url.toString());

  sse.addEventListener("done", () => { sse.close(); });
  sse.addEventListener("error", () => {
    if (sse.readyState === EventSource.CLOSED) sse.close();
  });

  setTimeout(() => {
    if (sse && sse.readyState !== EventSource.CLOSED) sse.close();
  }, 60000);
}

// --- Modals ---

function showHealthDetails(instanceId) {
  if (!currentHealthData) return;
  const instance = currentHealthData.instances.find(i => i.instance_id === instanceId);
  if (!instance) return;

  document.getElementById("healthDetailsInstance").textContent = instance.name;
  document.getElementById("healthDetailsContent").textContent = JSON.stringify(instance, null, 2);

  const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById("healthDetailsModal"));
  modal.show();
}

async function showBirtDebug(instanceId, instanceName) {
  const modalEl = document.getElementById("birtDebugModal");
  if (!modalEl) return;

  const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
  const titleEl = document.getElementById("birtDebugInstance");
  const contentEl = document.getElementById("birtDebugContent");
  const loadingEl = document.getElementById("birtDebugLoading");

  titleEl.textContent = instanceName;
  contentEl.textContent = "";
  loadingEl.classList.remove("d-none");

  modal.show();

  try {
    const { data } = await axios.get(`/status/debug/${instanceId}?debug_type=pools`);
    loadingEl.classList.add("d-none");

    if (data.status === "Success") {
      contentEl.textContent = data.output || "No pool data available";
    } else {
      contentEl.innerHTML = `<span class="text-danger">Error: ${escapeHtml(data.error || data.status)}</span>`;
    }
  } catch (e) {
    loadingEl.classList.add("d-none");
    contentEl.innerHTML = `<span class="text-danger">Failed to fetch: ${escapeHtml(e.response?.data?.detail || e.message)}</span>`;
  }
}

// =============================================
// BIRT Report Analytics
// =============================================

let birtAnalyticsSection, birtAnalyticsSummary, birtAnalyticsTabs;
let birtAnalyticsHead, birtAnalyticsBody, birtAnalyticsTable;
let birtAnalyticsLoading, birtAnalyticsEmpty;
let btnRefreshAnalytics, birtCustomRange, birtRangeFrom, birtRangeTo;
let selBirtServer, txtBirtReportSearch;
let birtPageInfo, birtPageNav, birtAnalyticsPagination;
let currentAnalyticsType = 'recent';
let isLoadingAnalytics = false;

// Sort + pagination state
let analyticsResults = [];       // full result set from last query
let analyticsSortField = null;
let analyticsSortAsc = true;
let analyticsPage = 1;
const PAGE_SIZE = 15;

// Debounce timer for report search and numeric inputs
let reportSearchTimer = null;
let numericFilterTimer = null;

// Cache of recently-running reports to bridge the gap between
// active-reports disappearing and CloudWatch ingesting the completion log
let _lastSeenRunning = {};  // key: "server|report|tenant" → { row, disappearedAt }
const _COMPLETING_GRACE_MS = 90_000;  // show "Completing" for up to 90s
let _analyticsAutoRefreshTimer = null;
const _AUTO_REFRESH_INTERVAL = 10_000;  // 10s auto-refresh while reports are running/completing

// New filter elements
let selBirtTenant = null;
let selBirtStatus = null;
let txtBirtSizeMin = null;
let txtBirtSizeMax = null;
let txtBirtDurationMin = null;
let txtBirtDurationMax = null;

function initBirtAnalytics() {
  birtAnalyticsSection = document.getElementById('birtAnalyticsSection');
  birtAnalyticsSummary = document.getElementById('birtAnalyticsSummary');
  birtAnalyticsTabs = document.getElementById('birtAnalyticsTabs');
  birtAnalyticsHead = document.getElementById('birtAnalyticsHead');
  birtAnalyticsBody = document.getElementById('birtAnalyticsBody');
  birtAnalyticsTable = document.getElementById('birtAnalyticsTable');
  birtAnalyticsLoading = document.getElementById('birtAnalyticsLoading');
  birtAnalyticsEmpty = document.getElementById('birtAnalyticsEmpty');
  btnRefreshAnalytics = document.getElementById('btnRefreshAnalytics');
  birtCustomRange = document.getElementById('birtCustomRange');
  birtRangeFrom = document.getElementById('birtRangeFrom');
  birtRangeTo = document.getElementById('birtRangeTo');
  selBirtServer = document.getElementById('selBirtServer');
  selBirtTenant = document.getElementById('selBirtTenant');
  selBirtStatus = document.getElementById('selBirtStatus');
  txtBirtReportSearch = document.getElementById('txtBirtReportSearch');
  txtBirtSizeMin = document.getElementById('txtBirtSizeMin');
  txtBirtSizeMax = document.getElementById('txtBirtSizeMax');
  txtBirtDurationMin = document.getElementById('txtBirtDurationMin');
  txtBirtDurationMax = document.getElementById('txtBirtDurationMax');
  birtPageInfo = document.getElementById('birtPageInfo');
  birtPageNav = document.getElementById('birtPageNav');
  birtAnalyticsPagination = document.getElementById('birtAnalyticsPagination');

  if (!birtAnalyticsSection) return;

  // Collapse/expand toggle
  const birtToggle = document.getElementById('birtAnalyticsToggle');
  birtToggle && birtToggle.addEventListener('click', () => {
    birtAnalyticsSection.classList.toggle('collapsed');
  });

  // Set default custom range dates
  if (birtRangeTo) birtRangeTo.value = new Date().toISOString().slice(0, 10);
  if (birtRangeFrom) {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    birtRangeFrom.value = d.toISOString().slice(0, 10);
  }

  // Time range radio change — also refresh tenant dropdown
  document.querySelectorAll('input[name="birtRange"]').forEach(radio => {
    radio.addEventListener('change', () => {
      if (radio.value === 'custom') {
        birtCustomRange.classList.remove('d-none');
      } else {
        birtCustomRange.classList.add('d-none');
        reloadAnalytics();
        loadBirtTenants();
      }
    });
  });

  // Custom date change
  if (birtRangeFrom) birtRangeFrom.addEventListener('change', () => { reloadAnalytics(); loadBirtTenants(); });
  if (birtRangeTo) birtRangeTo.addEventListener('change', () => { reloadAnalytics(); loadBirtTenants(); });

  // Dropdown filter changes (immediate) — server/status also refresh tenant list
  selBirtServer && selBirtServer.addEventListener('change', () => { reloadAnalytics(); loadBirtTenants(); });
  selBirtTenant && selBirtTenant.addEventListener('change', () => reloadAnalytics());
  selBirtStatus && selBirtStatus.addEventListener('change', () => { reloadAnalytics(); loadBirtTenants(); });

  // Report search with debounce (500ms)
  txtBirtReportSearch && txtBirtReportSearch.addEventListener('input', () => {
    clearTimeout(reportSearchTimer);
    reportSearchTimer = setTimeout(() => reloadAnalytics(), 500);
  });

  // Numeric filter inputs with debounce (800ms)
  [txtBirtSizeMin, txtBirtSizeMax, txtBirtDurationMin, txtBirtDurationMax].forEach(el => {
    el && el.addEventListener('input', () => {
      clearTimeout(numericFilterTimer);
      numericFilterTimer = setTimeout(() => reloadAnalytics(), 800);
    });
  });

  // Tab clicks
  birtAnalyticsTabs && birtAnalyticsTabs.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-type]');
    if (!btn) return;
    birtAnalyticsTabs.querySelectorAll('.birt-tab').forEach(l => l.classList.remove('active'));
    btn.classList.add('active');
    currentAnalyticsType = btn.dataset.type;
    analyticsSortField = null;
    analyticsPage = 1;
    loadBirtAnalytics(currentAnalyticsType);
  });

  // Refresh button
  btnRefreshAnalytics && btnRefreshAnalytics.addEventListener('click', () => reloadAnalytics());

  // Pagination click delegation
  birtPageNav && birtPageNav.addEventListener('click', (e) => {
    e.preventDefault();
    const link = e.target.closest('[data-page]');
    if (!link) return;
    analyticsPage = parseInt(link.dataset.page);
    renderCurrentPage();
  });
}

function reloadAnalytics() {
  analyticsPage = 1;
  loadBirtAnalytics(currentAnalyticsType);
  loadBirtAnalyticsSummary();
}

function showBirtAnalytics(show) {
  if (!birtAnalyticsSection) return;
  if (show) {
    birtAnalyticsSection.classList.remove('d-none');
    loadBirtServers();
    loadBirtTenants();
    loadBirtAnalyticsSummary();
    loadBirtAnalytics(currentAnalyticsType);
  } else {
    birtAnalyticsSection.classList.add('d-none');
  }
}

async function loadBirtServers() {
  if (!selBirtServer) return;
  try {
    const { data } = await axios.get('/birt/servers');
    const current = selBirtServer.value;
    selBirtServer.innerHTML = '<option value="">All Servers</option>';
    (data.servers || []).forEach(s => {
      const opt = document.createElement('option');
      opt.value = s;
      opt.textContent = s;
      selBirtServer.appendChild(opt);
    });
    if (current) selBirtServer.value = current;
  } catch (e) {
    // Hide analytics section if user lacks BIRT analytics permission
    if (e.response?.status === 403) {
      showBirtAnalytics(false);
      return;
    }
    console.error('Failed to load BIRT servers:', e);
  }
}

async function loadBirtTenants() {
  if (!selBirtTenant) return;
  // Build params from current time range, server, and status filters
  const params = {};
  const selectedRadio = document.querySelector('input[name="birtRange"]:checked');
  const val = selectedRadio ? selectedRadio.value : '1';
  if (val === 'custom' && birtRangeFrom && birtRangeTo && birtRangeFrom.value && birtRangeTo.value) {
    const from = new Date(birtRangeFrom.value);
    const to = new Date(birtRangeTo.value);
    to.setHours(23, 59, 59, 999);
    params.start_time = from.getTime();
    params.end_time = to.getTime();
  } else if (val !== 'custom') {
    params.hours = parseInt(val) || 1;
  }
  if (selBirtServer && selBirtServer.value) params.server = selBirtServer.value;
  if (selBirtStatus && selBirtStatus.value && selBirtStatus.value !== 'in_progress') params.status = selBirtStatus.value;
  try {
    const { data } = await axios.get('/birt/tenants', { params });
    const current = selBirtTenant.value;
    selBirtTenant.innerHTML = '<option value="">All Tenants</option>';
    (data.tenants || []).forEach(t => {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t.replace('orgcust_prod_', '').replace(/_/g, ' ');
      selBirtTenant.appendChild(opt);
    });
    // Preserve selection if still in list, otherwise reset
    if (current && [...selBirtTenant.options].some(o => o.value === current)) {
      selBirtTenant.value = current;
    }
  } catch (e) {
    console.error('Failed to load BIRT tenants:', e);
  }
}

function getAnalyticsParams() {
  const selectedRadio = document.querySelector('input[name="birtRange"]:checked');
  const val = selectedRadio ? selectedRadio.value : '1';
  const params = {};

  if (val === 'custom' && birtRangeFrom && birtRangeTo) {
    const from = new Date(birtRangeFrom.value);
    const to = new Date(birtRangeTo.value);
    to.setHours(23, 59, 59, 999);
    params.start_time = from.getTime();
    params.end_time = to.getTime();
  } else {
    params.hours = parseInt(val) || 1;
  }

  if (selBirtServer && selBirtServer.value) params.server = selBirtServer.value;
  if (txtBirtReportSearch && txtBirtReportSearch.value.trim()) params.report = txtBirtReportSearch.value.trim();
  if (selBirtTenant && selBirtTenant.value) params.tenant = selBirtTenant.value;
  if (selBirtStatus && selBirtStatus.value) params.status = selBirtStatus.value;
  if (txtBirtSizeMin && txtBirtSizeMin.value) params.size_min = parseFloat(txtBirtSizeMin.value);
  if (txtBirtSizeMax && txtBirtSizeMax.value) params.size_max = parseFloat(txtBirtSizeMax.value);
  if (txtBirtDurationMin && txtBirtDurationMin.value) params.duration_min = parseFloat(txtBirtDurationMin.value);
  if (txtBirtDurationMax && txtBirtDurationMax.value) params.duration_max = parseFloat(txtBirtDurationMax.value);

  return params;
}

async function loadBirtAnalyticsSummary() {
  const params = { type: 'summary', ...getAnalyticsParams() };
  try {
    const { data } = await axios.get('/birt/analytics', { params });
    renderAnalyticsSummary(data.results || []);
  } catch (e) {
    if (e.response?.status === 403) { showBirtAnalytics(false); return; }
    console.error('Failed to load BIRT analytics summary:', e);
    if (birtAnalyticsSummary) birtAnalyticsSummary.innerHTML = '';
  }
}

function renderAnalyticsSummary(rows) {
  if (!birtAnalyticsSummary) return;

  let totalExec = 0, totalSize = 0, totalGcTime = 0, tenantCount = 0;
  let maxP95Duration = 0;
  const tenants = new Set();

  rows.forEach(r => {
    totalExec += (r.executions || 0);
    totalSize += (r.totalSizeKB || 0);
    totalGcTime += (r.totalGcTimeMs || 0);
    if ((r.p95DurationMs || 0) > maxP95Duration) maxP95Duration = r.p95DurationMs;
    if (r.tenant) tenants.add(r.tenant);
  });
  tenantCount = tenants.size;

  const cards = [
    { icon: 'bi-file-earmark-bar-graph', label: 'Total Reports', value: totalExec, cls: 'birt-stat-primary' },
    { icon: 'bi-people', label: 'Tenants', value: tenantCount, cls: 'birt-stat-info' },
    { icon: 'bi-hdd', label: 'Total Output', value: formatSize(totalSize), cls: 'birt-stat-success' },
    { icon: 'bi-clock', label: 'P95 Duration', value: formatDuration(maxP95Duration), cls: 'birt-stat-warning' },
    { icon: 'bi-recycle', label: 'GC Time', value: formatDuration(totalGcTime), cls: 'birt-stat-danger' },
  ];

  birtAnalyticsSummary.innerHTML = cards.map(c => `
    <div class="birt-stat-card ${c.cls}">
      <i class="bi ${c.icon}"></i>
      <div class="birt-stat-value">${c.value}</div>
      <div class="birt-stat-label">${c.label}</div>
    </div>
  `).join('');
}

async function loadBirtAnalytics(type) {
  if (isLoadingAnalytics) return;
  isLoadingAnalytics = true;

  birtAnalyticsLoading && birtAnalyticsLoading.classList.remove('d-none');
  birtAnalyticsEmpty && birtAnalyticsEmpty.classList.add('d-none');
  birtAnalyticsTable && birtAnalyticsTable.classList.add('d-none');
  if (birtAnalyticsPagination) birtAnalyticsPagination.classList.add('d-none');

  const params = { type, ...getAnalyticsParams() };
  const statusFilter = params.status;
  try {
    // For "recent" tab, also fetch in-progress reports
    let activeReports = [];
    if (type === 'recent' && (!statusFilter || statusFilter === 'in_progress')) {
      // Fetch independently so one failure doesn't kill the other
      const analyticsPromise = statusFilter === 'in_progress'
        ? Promise.resolve({ data: { results: [] } })
        : axios.get('/birt/analytics', { params }).catch(e => { console.error('Analytics query failed:', e); return { data: { results: [] } }; });
      const activePromise = axios.get('/birt/active-reports').catch(e => { console.error('Active reports fetch failed:', e); return { data: { reports: [] } }; });

      const [analyticsResp, activeResp] = await Promise.all([analyticsPromise, activePromise]);
      analyticsResults = analyticsResp.data.results || [];
      activeReports = (activeResp.data.reports || []).map(r => ({
        ...r,
        '@timestamp': r.startedAt || new Date().toISOString(),
        status: 'in_progress',
      }));
      // Apply current filters to active reports (server, tenant, report name)
      if (params.server) activeReports = activeReports.filter(r => r.server === params.server);
      if (params.tenant) activeReports = activeReports.filter(r => r.tenant === params.tenant);
      if (params.report) activeReports = activeReports.filter(r => r.report && r.report.toLowerCase().includes(params.report.toLowerCase()));
    } else {
      const { data } = await axios.get('/birt/analytics', { params });
      analyticsResults = data.results || [];
    }

    // Track running → completing transitions
    const now = Date.now();
    const activeKeys = new Set(activeReports.map(r => `${r.server}|${r.report}|${r.tenant}`));

    // Remember currently running reports
    for (const r of activeReports) {
      _lastSeenRunning[`${r.server}|${r.report}|${r.tenant}`] = { row: r, disappearedAt: null };
    }

    // Reports that were running but no longer active → mark as completing
    const completingReports = [];
    for (const [key, entry] of Object.entries(_lastSeenRunning)) {
      if (activeKeys.has(key)) continue; // still running
      if (!entry.disappearedAt) entry.disappearedAt = now;
      // Check if it appeared in CloudWatch results
      const [srv, rpt, tnt] = key.split('|');
      const inCW = analyticsResults.some(r => r.server === srv && r.report === rpt && r.tenant === tnt);
      if (inCW || now - entry.disappearedAt > _COMPLETING_GRACE_MS) {
        delete _lastSeenRunning[key]; // found in CW or grace period expired
      } else {
        completingReports.push({ ...entry.row, status: 'completing' });
      }
    }

    // Filter completing reports by current filters too
    let filteredCompleting = completingReports;
    if (params.server) filteredCompleting = filteredCompleting.filter(r => r.server === params.server);
    if (params.tenant) filteredCompleting = filteredCompleting.filter(r => r.tenant === params.tenant);
    if (params.report) filteredCompleting = filteredCompleting.filter(r => r.report && r.report.toLowerCase().includes(params.report.toLowerCase()));

    // Merge: running first, then completing, then completed
    analyticsResults = [...activeReports, ...filteredCompleting, ...analyticsResults];
    analyticsSortField = null;

    birtAnalyticsLoading.classList.add('d-none');

    if (analyticsResults.length === 0) {
      const emptyMsgs = {
        failed: 'No failed or partial reports in this time range.',
        gc_heavy: 'No GC-heavy reports in this time range.',
        slowest: 'No reports found in this time range.',
        frequent: 'No reports found in this time range.',
        recent: 'No reports found in this time range.',
        summary: 'No report activity in this time range.',
      };
      birtAnalyticsEmpty.textContent = emptyMsgs[type] || emptyMsgs.recent;
      birtAnalyticsEmpty.classList.remove('d-none');
      birtAnalyticsTable.classList.add('d-none');
      if (birtAnalyticsPagination) birtAnalyticsPagination.classList.add('d-none');
      return;
    }

    birtAnalyticsTable.classList.remove('d-none');
    analyticsPage = 1;
    renderCurrentPage();

    // Auto-refresh while there are running or completing reports
    _scheduleAutoRefresh(type, activeReports, filteredCompleting);

  } catch (e) {
    if (e.response?.status === 403) { showBirtAnalytics(false); return; }
    console.error('Failed to load BIRT analytics:', e);
    birtAnalyticsLoading.classList.add('d-none');
    birtAnalyticsEmpty.classList.remove('d-none');
    if (birtAnalyticsEmpty) {
      birtAnalyticsEmpty.textContent = 'Failed to query analytics: ' + (e.response?.data?.detail || e.message);
    }
  } finally {
    isLoadingAnalytics = false;
  }
}

function _scheduleAutoRefresh(type, activeReports, completingReports) {
  // Clear any existing timer
  if (_analyticsAutoRefreshTimer) {
    clearTimeout(_analyticsAutoRefreshTimer);
    _analyticsAutoRefreshTimer = null;
  }
  // Only auto-refresh on "recent" tab when there are live rows
  if (type === 'recent' && (activeReports.length > 0 || completingReports.length > 0)) {
    _analyticsAutoRefreshTimer = setTimeout(() => {
      _analyticsAutoRefreshTimer = null;
      if (currentAnalyticsType === 'recent' && !isLoadingAnalytics) {
        loadBirtAnalytics('recent');
      }
    }, _AUTO_REFRESH_INTERVAL);
  }
}

function renderCurrentPage() {
  const columns = getColumnsForType(currentAnalyticsType);
  const rows = analyticsResults;
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  if (analyticsPage > totalPages) analyticsPage = totalPages;
  if (analyticsPage < 1) analyticsPage = 1;

  const start = (analyticsPage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);

  // Render header with sort indicators
  birtAnalyticsHead.innerHTML = '<tr>' + columns.map(c => {
    const isSorted = analyticsSortField === c.field;
    const arrow = isSorted ? (analyticsSortAsc ? ' <i class="bi bi-caret-up-fill"></i>' : ' <i class="bi bi-caret-down-fill"></i>') : '';
    return `<th role="button" data-sort-field="${c.field}" style="cursor:pointer;white-space:nowrap;user-select:none;">${c.label}${arrow}</th>`;
  }).join('') + '</tr>';

  // Bind sort click on header
  birtAnalyticsHead.querySelectorAll('[data-sort-field]').forEach(th => {
    th.addEventListener('click', () => {
      const field = th.dataset.sortField;
      if (analyticsSortField === field) {
        analyticsSortAsc = !analyticsSortAsc;
      } else {
        analyticsSortField = field;
        analyticsSortAsc = true;
      }
      sortAnalyticsResults(field, analyticsSortAsc);
      analyticsPage = 1;
      renderCurrentPage();
    });
  });

  // Render rows
  birtAnalyticsBody.innerHTML = pageRows.map(row => {
    return '<tr>' + columns.map(c => {
      let val = row[c.field];
      if (c.format) val = c.format(val, row);
      else if (val === undefined || val === null) val = '-';
      return `<td>${val}</td>`;
    }).join('') + '</tr>';
  }).join('');

  // Pagination
  if (birtAnalyticsPagination) {
    if (totalPages <= 1) {
      birtAnalyticsPagination.classList.add('d-none');
    } else {
      birtAnalyticsPagination.classList.remove('d-none');
    }
  }
  if (birtPageInfo) {
    birtPageInfo.textContent = `${start + 1}\u2013${Math.min(start + PAGE_SIZE, rows.length)} of ${rows.length}`;
  }
  if (birtPageNav) {
    let nav = '';
    nav += `<li class="page-item ${analyticsPage <= 1 ? 'disabled' : ''}"><a class="page-link" href="#" data-page="${analyticsPage - 1}">&laquo;</a></li>`;
    for (let p = 1; p <= totalPages; p++) {
      // Show max 7 page buttons with ellipsis
      if (totalPages > 7 && p > 2 && p < totalPages - 1 && Math.abs(p - analyticsPage) > 1) {
        if (p === 3 && analyticsPage > 4) { nav += '<li class="page-item disabled"><span class="page-link">&hellip;</span></li>'; continue; }
        if (p === totalPages - 2 && analyticsPage < totalPages - 3) { nav += '<li class="page-item disabled"><span class="page-link">&hellip;</span></li>'; continue; }
        if (Math.abs(p - analyticsPage) > 1) continue;
      }
      nav += `<li class="page-item ${p === analyticsPage ? 'active' : ''}"><a class="page-link" href="#" data-page="${p}">${p}</a></li>`;
    }
    nav += `<li class="page-item ${analyticsPage >= totalPages ? 'disabled' : ''}"><a class="page-link" href="#" data-page="${analyticsPage + 1}">&raquo;</a></li>`;
    birtPageNav.innerHTML = nav;
  }
}

function sortAnalyticsResults(field, asc) {
  analyticsResults.sort((a, b) => {
    // Running/completing reports always stay on top
    const aPriority = a.status === 'in_progress' ? 2 : a.status === 'completing' ? 1 : 0;
    const bPriority = b.status === 'in_progress' ? 2 : b.status === 'completing' ? 1 : 0;
    if (aPriority !== bPriority) return bPriority - aPriority;
    let va = a[field], vb = b[field];
    if (va === undefined || va === null) va = '';
    if (vb === undefined || vb === null) vb = '';
    // Numeric comparison
    if (typeof va === 'number' && typeof vb === 'number') return asc ? va - vb : vb - va;
    // String comparison
    va = String(va).toLowerCase();
    vb = String(vb).toLowerCase();
    if (va < vb) return asc ? -1 : 1;
    if (va > vb) return asc ? 1 : -1;
    return 0;
  });
}

function getColumnsForType(type) {
  const fmtDur = v => formatDuration(v);
  const fmtSize = v => formatSize(v);
  const fmtTs = v => {
    if (!v) return '-';
    // CloudWatch @timestamp is UTC but lacks Z suffix
    const utc = v.endsWith('Z') || v.includes('+') ? v : v.replace(' ', 'T') + 'Z';
    return new Date(utc).toLocaleString('en-US', { timeZone: 'Africa/Cairo', hour12: true });
  };
  const fmtStatus = (v, row) => {
    let badge = '';
    if (v === 'in_progress') badge = '<span class="badge bg-info"><i class="bi bi-arrow-repeat spin"></i> Running</span>';
    else if (v === 'completing') badge = '<span class="badge bg-info bg-opacity-50"><i class="bi bi-hourglass-split"></i> Completing</span>';
    else if (v === 'success') badge = '<span class="badge bg-success">Success</span>';
    else if (v === 'failed') badge = '<span class="badge bg-danger">Failed</span>';
    else if (v === 'partial') badge = '<span class="badge bg-warning text-dark">Partial</span>';
    else if (v === 'cancelled') badge = '<span class="badge bg-secondary"><i class="bi bi-x-circle"></i> Cancelled</span>';
    else badge = escapeHtml(String(v || '-'));
    // Dedup indicator
    if (row && row.dedup === 'hit') badge += ' <span class="badge bg-info bg-opacity-25" title="Deduplicated — piggybacked on existing execution"><i class="bi bi-copy"></i></span>';
    if (row && row.waiters > 0) badge += ` <span class="badge bg-info bg-opacity-25" title="${row.waiters} duplicate request(s) waiting"><i class="bi bi-people-fill"></i> ${row.waiters}</span>`;
    return badge;
  };

  switch (type) {
    case 'recent':
    case 'slowest':
      return [
        { field: '@timestamp', label: 'Time', format: fmtTs },
        { field: 'server', label: 'Server' },
        { field: 'report', label: 'Report' },
        { field: 'type', label: 'Type', format: v => v ? v.toUpperCase() : '-' },
        { field: 'tenant', label: 'Tenant' },
        { field: 'durationMs', label: 'Duration', format: fmtDur },
        { field: 'sizeKB', label: 'Size', format: fmtSize },
        { field: 'cpuMs', label: 'CPU', format: fmtDur },
        { field: 'gcCount', label: 'GC' },
        { field: 'gcTimeMs', label: 'GC Time', format: fmtDur },
        { field: 'status', label: 'Status', format: (v, row) => fmtStatus(v, row) },
        { field: '_actions', label: '', format: (_, row) => _fmtInspectBtn(row) },
      ];
    case 'gc_heavy':
      return [
        { field: '@timestamp', label: 'Time', format: fmtTs },
        { field: 'server', label: 'Server' },
        { field: 'report', label: 'Report' },
        { field: 'type', label: 'Type', format: v => v ? v.toUpperCase() : '-' },
        { field: 'tenant', label: 'Tenant' },
        { field: 'gcCount', label: 'GC Count' },
        { field: 'gcTimeMs', label: 'GC Time', format: fmtDur },
        { field: 'durationMs', label: 'Duration', format: fmtDur },
        { field: 'memBeforeMB', label: 'Mem Before', format: v => v != null ? v + ' MB' : '-' },
        { field: 'memAfterMB', label: 'Mem After', format: v => v != null ? v + ' MB' : '-' },
        { field: '_actions', label: '', format: (_, row) => _fmtInspectBtn(row) },
      ];
    case 'failed':
      return [
        { field: '@timestamp', label: 'Time', format: fmtTs },
        { field: 'server', label: 'Server' },
        { field: 'report', label: 'Report' },
        { field: 'tenant', label: 'Tenant' },
        { field: 'durationMs', label: 'Duration', format: fmtDur },
        { field: 'type', label: 'Type' },
        { field: 'concurrent', label: 'Concurrent' },
        { field: 'status', label: 'Status', format: (v, row) => fmtStatus(v, row) },
        { field: '_actions', label: '', format: (_, row) => _fmtInspectBtn(row) },
      ];
    case 'frequent':
      return [
        { field: 'server', label: 'Server' },
        { field: 'report', label: 'Report' },
        { field: 'tenant', label: 'Tenant' },
        { field: 'executions', label: 'Executions' },
        { field: 'avgDurationMs', label: 'Avg Duration', format: fmtDur },
        { field: 'totalSizeKB', label: 'Total Size', format: fmtSize },
      ];
    case 'summary':
      return [
        { field: 'server', label: 'Server' },
        { field: 'tenant', label: 'Tenant' },
        { field: 'executions', label: 'Executions' },
        { field: 'uniqueReports', label: 'Unique Reports' },
        { field: 'avgDurationMs', label: 'Avg Duration', format: fmtDur },
        { field: 'maxDurationMs', label: 'Max Duration', format: fmtDur },
        { field: 'totalSizeKB', label: 'Total Size', format: fmtSize },
        { field: 'totalGcTimeMs', label: 'GC Time', format: fmtDur },
      ];
    default:
      return [{ field: '@timestamp', label: 'Time', format: fmtTs }, { field: 'report', label: 'Report' }];
  }
}

function formatDuration(ms) {
  if (ms === undefined || ms === null || ms < 0) return '-';
  if (ms < 1000) return ms + 'ms';
  if (ms < 60000) return (ms / 1000).toFixed(1) + 's';
  return (ms / 60000).toFixed(1) + 'm';
}

function formatSize(kb) {
  if (kb === undefined || kb === null) return '-';
  if (kb < 1024) return kb + ' KB';
  return (kb / 1024).toFixed(1) + ' MB';
}

// =============================================
// Report Detail Inspector
// =============================================

function _fmtInspectBtn(row) {
  if (!row.server) return '';
  const srv = escapeHtml(row.server);
  const rpt = escapeHtml(row.report || '');
  const tnt = escapeHtml(row.tenant || '');
  const isRunning = row.status === 'in_progress' || row.status === 'completing';
  // For finished rows, only show if filter, params, or dataSetCount present
  if (!isRunning && !row.filter && !row.params && !row.dataSetCount) return '';
  const icon = isRunning ? 'bi-code-square' : 'bi-funnel';
  const title = isRunning ? 'Inspect queries & parameters' : 'View filter & parameters';
  const rowIdx = analyticsResults.indexOf(row);
  return `<button class="btn btn-sm btn-outline-info py-0 px-1" title="${title}" onclick="window._showReportDetail('${srv}','${rpt}','${tnt}',${isRunning},${rowIdx})"><i class="bi ${icon}"></i></button>`;
}

const _SQL_KEYWORDS = /\b(SELECT|FROM|WHERE|AND|OR|IN|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|GROUP|BY|ORDER|HAVING|LIMIT|INSERT|UPDATE|DELETE|SET|VALUES|INTO|CREATE|ALTER|DROP|TABLE|INDEX|NOT|NULL|IS|LIKE|BETWEEN|EXISTS|CASE|WHEN|THEN|ELSE|END|UNION|ALL|DISTINCT|COUNT|SUM|AVG|MAX|MIN|CAST|CONVERT|COALESCE|TOP|OFFSET|FETCH|NEXT|ROWS|ONLY|WITH|ASC|DESC)\b/gi;

function _highlightSQL(sql) {
  return escapeHtml(sql).replace(_SQL_KEYWORDS, '<span class="kw">$1</span>');
}

function _parseParamKey(key) {
  const parts = key.split('#_#');
  return { name: parts[0] || key, type: parts.length > 1 ? parts.slice(1).join(' ') : '' };
}

function _renderRunningReportDetail(report, canCancel, server) {
  const dur = report.runningSec != null ? (report.runningSec < 60 ? report.runningSec + 's' : (report.runningSec / 60).toFixed(1) + 'm') : '-';

  let html = `<div class="rd-overview">
    <div class="rd-field"><span class="rd-label">Report</span><span class="rd-value">${escapeHtml(report.reportName || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Type</span><span class="rd-value">${escapeHtml((report.reportType || '').toUpperCase())}</span></div>
    <div class="rd-field"><span class="rd-label">Tenant</span><span class="rd-value">${escapeHtml(report.tenant || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Server</span><span class="rd-value">${escapeHtml(server)}</span></div>
    <div class="rd-field"><span class="rd-label">Thread</span><span class="rd-value" style="font-family:'JetBrains Mono',monospace;font-size:0.85rem">${escapeHtml(report.thread || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Running</span><span class="rd-value"><span class="rd-badge rd-badge-running"><i class="bi bi-arrow-repeat spin"></i> ${dur}</span></span></div>
    ${report.waiters > 0 ? `<div class="rd-field"><span class="rd-label">Waiters</span><span class="rd-value"><span class="badge bg-info bg-opacity-25"><i class="bi bi-people-fill me-1"></i>${report.waiters} duplicate request(s) piggybacking</span></span></div>` : ''}
  </div>`;

  // SQL Queries
  html += `<div class="rd-section"><div class="rd-section-title"><i class="bi bi-database"></i> SQL Queries</div>`;
  if (report.queries && Object.keys(report.queries).length > 0) {
    for (const [dsName, sql] of Object.entries(report.queries)) {
      const sqlId = 'sql_' + dsName.replace(/\W/g, '_');
      html += `<div class="rd-sql-card">
        <div class="rd-sql-header"><span><i class="bi bi-table me-1"></i>${escapeHtml(dsName)}</span><button class="rd-copy-btn" onclick="navigator.clipboard.writeText(document.getElementById('${sqlId}').dataset.raw);this.textContent='Copied!';setTimeout(()=>this.textContent='Copy',1500)">Copy</button></div>
        <div class="rd-sql-body" id="${sqlId}" data-raw="${escapeHtml(sql)}">${_highlightSQL(sql)}</div>
      </div>`;
    }
  } else {
    html += `<span class="rd-empty">No SQL datasets captured</span>`;
  }
  html += `</div>`;

  // Parameters
  html += `<div class="rd-section"><div class="rd-section-title"><i class="bi bi-sliders"></i> Parameters</div>`;
  if (report.parameters && Object.keys(report.parameters).length > 0) {
    html += `<table class="rd-params-table"><thead><tr><th>Parameter</th><th>Type</th><th>Value</th></tr></thead><tbody>`;
    for (const [key, val] of Object.entries(report.parameters)) {
      const p = _parseParamKey(key);
      html += `<tr><td>${escapeHtml(p.name)}</td><td class="rd-param-type">${escapeHtml(p.type)}</td><td>${escapeHtml(String(val))}</td></tr>`;
    }
    html += `</tbody></table>`;
  } else {
    html += `<span class="rd-empty">No parameters</span>`;
  }
  html += `</div>`;

  // WHERE conditions
  if (report.whereConditions) {
    html += `<div class="rd-section"><div class="rd-section-title"><i class="bi bi-funnel"></i> WHERE Filter</div>
      <div class="rd-where">${_highlightSQL(report.whereConditions)}</div></div>`;
  }

  // Footer: cancel button
  const footerEl = document.getElementById('reportDetailFooterLeft');
  if (footerEl) {
    if (canCancel && report.id) {
      footerEl.innerHTML = `<button class="btn btn-sm btn-outline-danger" onclick="window._cancelReport('${escapeHtml(server)}','${escapeHtml(report.id)}','${escapeHtml(report.reportName || '')}')"><i class="bi bi-x-octagon me-1"></i>Cancel Report</button>`;
    } else {
      footerEl.innerHTML = '';
    }
  }

  return html;
}

function _renderFinishedReportDetail(row) {
  const dur = row.durationMs != null ? formatDuration(row.durationMs) : '-';

  let html = `<div class="rd-overview">
    <div class="rd-field"><span class="rd-label">Report</span><span class="rd-value">${escapeHtml(row.report || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Type</span><span class="rd-value">${escapeHtml((row.type || '').toUpperCase())}</span></div>
    <div class="rd-field"><span class="rd-label">Tenant</span><span class="rd-value">${escapeHtml(row.tenant || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Server</span><span class="rd-value">${escapeHtml(row.server || '')}</span></div>
    <div class="rd-field"><span class="rd-label">Duration</span><span class="rd-value">${dur}</span></div>
    <div class="rd-field"><span class="rd-label">Status</span><span class="rd-value"><span class="rd-badge rd-badge-finished">${escapeHtml(row.status || '')}</span></span></div>
    ${row.dataSetCount ? `<div class="rd-field"><span class="rd-label">DataSets</span><span class="rd-value">${row.dataSetCount}</span></div>` : ''}
    ${row.dedup === 'hit' ? `<div class="rd-field"><span class="rd-label">Dedup</span><span class="rd-value"><span class="badge bg-info bg-opacity-25"><i class="bi bi-copy me-1"></i>Piggybacked</span>${row.waitTimeMs != null ? ` <span class="text-muted ms-2">waited ${formatDuration(row.waitTimeMs)}</span>` : ''}</span></div>` : ''}
  </div>`;

  // Parameters from CW log (raw key:value pairs)
  if (row.params) {
    html += `<div class="rd-section"><div class="rd-section-title"><i class="bi bi-sliders"></i> Parameters</div>`;
    html += `<table class="rd-params-table"><thead><tr><th>Parameter</th><th>Value</th></tr></thead><tbody>`;
    // params is a raw string like: "cpdbid":"500050535","lang":"23"
    const paramPairs = row.params.match(/"([^"]+)":"([^"]*)"/g) || [];
    for (const pair of paramPairs) {
      const m = pair.match(/"([^"]+)":"([^"]*)"/);
      if (m) html += `<tr><td>${escapeHtml(m[1])}</td><td>${escapeHtml(m[2])}</td></tr>`;
    }
    html += `</tbody></table></div>`;
  }

  // WHERE filter from CW log
  if (row.filter) {
    html += `<div class="rd-section"><div class="rd-section-title"><i class="bi bi-funnel"></i> WHERE Filter</div>
      <div class="rd-where">${_highlightSQL(row.filter)}</div></div>`;
  }

  // Note about limited data
  html += `<div class="rd-section"><span class="rd-empty"><i class="bi bi-info-circle me-1"></i>Full SQL queries are only available for running reports. Finished reports show filter and metadata from CloudWatch logs.</span></div>`;

  const footerEl = document.getElementById('reportDetailFooterLeft');
  if (footerEl) footerEl.innerHTML = '';

  return html;
}

window._showReportDetail = async function(server, report, tenant, isRunning, rowIdx) {
  const modal = new bootstrap.Modal(document.getElementById('reportDetailModal'));
  const loading = document.getElementById('reportDetailLoading');
  const content = document.getElementById('reportDetailContent');
  const title = document.getElementById('reportDetailTitle');

  title.innerHTML = `<i class="bi bi-file-earmark-code me-2"></i>${escapeHtml(report || 'Report Inspector')}`;
  loading.style.display = '';
  content.style.display = 'none';
  content.innerHTML = '';
  const footerEl = document.getElementById('reportDetailFooterLeft');
  if (footerEl) footerEl.innerHTML = '';
  modal.show();

  if (isRunning) {
    // Fetch live details from BIRT server
    try {
      const { data } = await axios.get(`/birt/report-details/${encodeURIComponent(server)}`);
      const reports = data.reports || [];
      // Match by reportName + tenant
      const match = reports.find(r =>
        r.reportName === report && r.tenant === tenant
      ) || reports.find(r => r.reportName === report) || reports[0];

      if (match) {
        content.innerHTML = _renderRunningReportDetail(match, data.can_cancel, server);
      } else {
        content.innerHTML = `<div class="p-4 text-center"><span class="rd-empty">Report no longer running on ${escapeHtml(server)}</span></div>`;
      }
    } catch (e) {
      content.innerHTML = `<div class="p-4 text-center text-danger"><i class="bi bi-exclamation-triangle me-2"></i>${escapeHtml(e.response?.data?.detail || e.message)}</div>`;
    }
  } else {
    // Finished report — show data from CloudWatch log row
    const row = analyticsResults[rowIdx];
    if (row) {
      content.innerHTML = _renderFinishedReportDetail(row);
    } else {
      content.innerHTML = `<div class="p-4 text-center"><span class="rd-empty">Report data not available</span></div>`;
    }
  }

  loading.style.display = 'none';
  content.style.display = '';
};

window._cancelReport = async function(server, reportId, reportName) {
  if (!confirm(`Cancel report "${reportName}" on ${server}?\n\nThis will terminate the running report.`)) return;
  try {
    const { data } = await axios.post(`/birt/report-cancel/${encodeURIComponent(server)}/${encodeURIComponent(reportId)}`);
    if (data.success) {
      window.toast?.success(`Report cancelled: ${reportName}`);
      bootstrap.Modal.getInstance(document.getElementById('reportDetailModal'))?.hide();
      // Refresh analytics
      setTimeout(() => loadBirtAnalytics(currentAnalyticsType), 1000);
    } else {
      window.toast?.error(data.error || 'Cancel failed');
    }
  } catch (e) {
    window.toast?.error(e.response?.data?.detail || 'Cancel request failed');
  }
};

export { stopHealthAutoRefresh };
